import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { Sparkles, X, Plus, Trash2, Users } from 'lucide-react';
import axios from 'axios';
import BootcampPaymentModal from './BootcampPaymentModal';
import BootcampCardCaptureModal from './BootcampCardCaptureModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankStudent = () => ({
  student_name: '',
  student_age: '',
  medical_info: '',
});

const INITIAL_PARENT = {
  parent_name: '',
  parent_email: '',
  parent_phone: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
};

const BootcampBookingModal = ({ bootcamp, open, onClose, onSuccess }) => {
  const [parent, setParent] = useState(INITIAL_PARENT);
  const [students, setStudents] = useState([blankStudent()]);
  const [submitting, setSubmitting] = useState(false);
  const [createdBookings, setCreatedBookings] = useState(null); // array
  const [showPayment, setShowPayment] = useState(false);
  const [showCardCapture, setShowCardCapture] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const handleParentChange = (field, value) =>
    setParent((p) => ({ ...p, [field]: value }));

  const handleStudentChange = (idx, field, value) =>
    setStudents((arr) => arr.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const addStudent = () => {
    if (students.length >= 5) {
      toast.error('Maximum 5 children per booking');
      return;
    }
    setStudents((arr) => [...arr, blankStudent()]);
  };

  const removeStudent = (idx) => {
    if (students.length <= 1) return;
    setStudents((arr) => arr.filter((_, i) => i !== idx));
  };

  // Pricing preview (frontend only — backend is source of truth on submit)
  const pricing = useMemo(() => {
    if (!bootcamp) return null;
    const base = Number(bootcamp.price || 0);
    const codeOff = discountApplied ? Number(discountApplied.amount_off || 0) : 0;
    const afterCode = Math.max(0, +(base - codeOff).toFixed(2));
    const lines = students.map((s, i) => {
      if (i === 0) {
        return { label: s.student_name || `Child ${i + 1}`, amount: afterCode, isSibling: false };
      }
      const sibOff = +(afterCode * 0.3).toFixed(2);
      const final = Math.max(0, +(afterCode - sibOff).toFixed(2));
      return { label: s.student_name || `Child ${i + 1}`, amount: final, isSibling: true, sibOff };
    });
    const total = +lines.reduce((acc, l) => acc + l.amount, 0).toFixed(2);
    return { lines, total, base, afterCode };
  }, [bootcamp, students, discountApplied]);

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidatingDiscount(true);
    try {
      const res = await axios.post(`${API_URL}/api/bootcamps/validate-discount`, {
        code: discountCode.trim(),
        bootcamp_id: bootcamp.id,
      });
      setDiscountApplied(res.data);
      toast.success(`Discount applied: £${res.data.amount_off.toFixed(2)} off (per child)`);
    } catch (err) {
      setDiscountApplied(null);
      toast.error(err.response?.data?.detail || 'Invalid discount code');
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
  };

  const validateForm = () => {
    if (!parent.parent_name || !parent.parent_email || !parent.parent_phone) {
      toast.error('Please fill in parent name, email and phone');
      return false;
    }
    for (let i = 0; i < students.length; i++) {
      const s = students[i];
      if (!s.student_name || !s.student_age) {
        toast.error(`Child ${i + 1}: name and age are required`);
        return false;
      }
      const age = parseInt(s.student_age, 10);
      if (Number.isNaN(age) || age < bootcamp.age_min || age > bootcamp.age_max) {
        toast.error(`${s.student_name || `Child ${i + 1}`}: age must be ${bootcamp.age_min}–${bootcamp.age_max}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!bootcamp) return;
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        bootcamp_id: bootcamp.id,
        ...parent,
        discount_code: discountApplied ? discountApplied.code : undefined,
        students: students.map((s) => ({
          student_name: s.student_name,
          student_age: parseInt(s.student_age, 10),
          medical_info: s.medical_info || undefined,
        })),
      };
      const res = await axios.post(`${API_URL}/api/bootcamps/book-siblings`, payload);
      const bookings = res.data.bookings || [];
      if (bookings.length > 1) {
        const saved = bookings.reduce(
          (acc, b) => acc + Number(b?.sibling_discount_info?.amount_off || 0),
          0,
        );
        toast.success(
          `${bookings.length} children booked${saved > 0 ? ` — £${saved.toFixed(2)} sibling discount` : ''}.`,
        );
      } else {
        toast.success(`${bookings[0]?.student_name || 'Child'} booked.`);
      }
      setCreatedBookings(bookings);
      setShowCardCapture(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentClose = () => setShowPayment(false);

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setShowCardCapture(false);
    setParent(INITIAL_PARENT);
    setStudents([blankStudent()]);
    setCreatedBookings(null);
    setDiscountCode('');
    setDiscountApplied(null);
    onClose();
    if (onSuccess) onSuccess();
  };

  if (!bootcamp) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] p-0 bg-neutral-900 border-neutral-800 overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-b border-neutral-800">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50"
            data-testid="close-booking-modal"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Book {bootcamp.name}</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm mt-1">
            {new Date(bootcamp.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} –{' '}
            {new Date(bootcamp.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-6 space-y-5">
            {/* Children */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Child{students.length > 1 ? `ren (${students.length})` : ''}
                </h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addStudent}
                  className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 h-7 px-2 text-xs"
                  data-testid="add-sibling-btn"
                  disabled={students.length >= 5}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add another child
                </Button>
              </div>

              {students.map((s, idx) => (
                <div
                  key={idx}
                  className="border border-neutral-800 rounded-lg p-4 space-y-3 bg-neutral-900/40"
                  data-testid={`student-card-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-white/50">
                      Child {idx + 1}
                      {idx > 0 && (
                        <span className="ml-2 text-pink-400 normal-case">— 30% sibling discount</span>
                      )}
                    </div>
                    {students.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStudent(idx)}
                        className="text-white/40 hover:text-red-400 text-xs flex items-center gap-1"
                        data-testid={`remove-sibling-${idx}`}
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-white text-xs">Student Name *</Label>
                      <Input
                        value={s.student_name}
                        onChange={(e) => handleStudentChange(idx, 'student_name', e.target.value)}
                        placeholder="Full name"
                        className="bg-neutral-800 border-neutral-700 text-white mt-1"
                        data-testid={`student-name-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label className="text-white text-xs">Age *</Label>
                      <Input
                        type="number"
                        value={s.student_age}
                        onChange={(e) => handleStudentChange(idx, 'student_age', e.target.value)}
                        placeholder={`${bootcamp.age_min}-${bootcamp.age_max}`}
                        min={bootcamp.age_min}
                        max={bootcamp.age_max}
                        className="bg-neutral-800 border-neutral-700 text-white mt-1"
                        data-testid={`student-age-input-${idx}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-xs">Medical / Allergies</Label>
                    <Textarea
                      value={s.medical_info}
                      onChange={(e) => handleStudentChange(idx, 'medical_info', e.target.value)}
                      placeholder="Any allergies, medical conditions, medications…"
                      className="bg-neutral-800 border-neutral-700 text-white mt-1 min-h-[60px]"
                      data-testid={`student-medical-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Parent / Guardian */}
            <div className="space-y-3 pt-3 border-t border-neutral-800">
              <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Parent / Guardian</h4>
              <div>
                <Label className="text-white text-xs">Name *</Label>
                <Input
                  value={parent.parent_name}
                  onChange={(e) => handleParentChange('parent_name', e.target.value)}
                  placeholder="Full name"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1"
                  data-testid="parent-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white text-xs">Email *</Label>
                  <Input
                    type="email"
                    value={parent.parent_email}
                    onChange={(e) => handleParentChange('parent_email', e.target.value)}
                    placeholder="email@example.com"
                    className="bg-neutral-800 border-neutral-700 text-white mt-1"
                    data-testid="parent-email-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white text-xs">Phone *</Label>
                  <Input
                    type="tel"
                    value={parent.parent_phone}
                    onChange={(e) => handleParentChange('parent_phone', e.target.value)}
                    placeholder="07700 000000"
                    className="bg-neutral-800 border-neutral-700 text-white mt-1"
                    data-testid="parent-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="space-y-3 pt-3 border-t border-neutral-800">
              <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Emergency Contact</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white text-xs">Name</Label>
                  <Input
                    value={parent.emergency_contact_name}
                    onChange={(e) => handleParentChange('emergency_contact_name', e.target.value)}
                    placeholder="Full name"
                    className="bg-neutral-800 border-neutral-700 text-white mt-1"
                    data-testid="emergency-name-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-white text-xs">Phone</Label>
                  <Input
                    type="tel"
                    value={parent.emergency_contact_phone}
                    onChange={(e) => handleParentChange('emergency_contact_phone', e.target.value)}
                    placeholder="07700 000000"
                    className="bg-neutral-800 border-neutral-700 text-white mt-1"
                    data-testid="emergency-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Discount Code */}
            <div className="space-y-3 pt-3 border-t border-neutral-800">
              <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Discount Code</h4>
              {!discountApplied ? (
                <div className="flex gap-2">
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="bg-neutral-800 border-neutral-700 text-white uppercase"
                    data-testid="bootcamp-discount-code-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validateDiscount(); } }}
                  />
                  <Button
                    onClick={validateDiscount}
                    disabled={validatingDiscount || !discountCode.trim()}
                    variant="outline"
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    data-testid="bootcamp-discount-apply-btn"
                  >
                    {validatingDiscount ? '...' : 'Apply'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-emerald-400 font-semibold text-sm" data-testid="bootcamp-discount-applied">
                      ✓ {discountApplied.code}
                    </div>
                    <div className="text-emerald-300/70 text-xs">
                      {discountApplied.name || `-£${discountApplied.amount_off.toFixed(2)}`} per child
                    </div>
                  </div>
                  <Button
                    onClick={removeDiscount}
                    variant="ghost"
                    size="sm"
                    className="text-emerald-400 hover:bg-emerald-500/20 h-7 px-2 text-xs"
                    data-testid="bootcamp-discount-remove-btn"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            {/* Pricing Breakdown */}
            {pricing && (
              <div className="space-y-2 pt-3 border-t border-neutral-800" data-testid="pricing-breakdown">
                <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Pricing</h4>
                {pricing.lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm" data-testid={`pricing-line-${i}`}>
                    <span className="text-white/70">
                      {l.label}
                      {l.isSibling && (
                        <span className="text-pink-400 ml-2 text-xs">(30% sibling: −£{l.sibOff.toFixed(2)})</span>
                      )}
                    </span>
                    <span className="text-white font-medium">£{l.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="p-6 bg-neutral-950 border-t border-neutral-800">
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="text-2xl font-bold text-white" data-testid="group-total">
                £{(pricing?.total ?? bootcamp.price).toFixed(2)}
              </span>
              <span className="text-white/50 ml-1 text-sm">total</span>
              {students.length > 1 && (
                <div className="text-white/50 text-xs">{students.length} children</div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="border-neutral-700 text-white hover:bg-neutral-800">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6"
                data-testid="submit-booking-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 w-4 h-4" />
                    Secure Your Place
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Card capture (save card, no charge) */}
      {showCardCapture && createdBookings && createdBookings.length > 0 && (
        <BootcampCardCaptureModal
          bookings={createdBookings}
          bootcamp={bootcamp}
          open={showCardCapture}
          onClose={() => setShowCardCapture(false)}
          onDone={handlePaymentSuccess}
          onPayNow={() => { setShowCardCapture(false); setShowPayment(true); }}
        />
      )}

      {/* Payment Modal */}
      {showPayment && createdBookings && createdBookings.length > 0 && (
        <BootcampPaymentModal
          bookings={createdBookings}
          bootcamp={bootcamp}
          open={showPayment}
          onClose={handlePaymentClose}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Dialog>
  );
};

export default BootcampBookingModal;
